import { useEffect, useMemo, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import type { ElectionState, RoundSettings, StateRace, ThresholdBase } from '../types';
import { activeCandidates, suggestedCandidateIds } from '../utils/rounds';
import { MinisterPhoto } from './MinisterPhoto';

interface RoundDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  state: ElectionState;
  positionId: string;
  positionTitle: string;
  /** Editing a draft round; otherwise a new one */
  race?: StateRace | null;
  busy: boolean;
  onSubmit: (candidateIds: string[], settings: RoundSettings) => void;
}

export function RoundDialog({ open, onOpenChange, state, positionId, positionTitle, race, busy, onSubmit }: RoundDialogProps) {
  const e = state.election;
  const pool = useMemo(() => activeCandidates(state), [state]);
  const [selected, setSelected] = useState<string[]>([]);
  const [topX, setTopX] = useState('');
  const [pctText, setPctText] = useState('');
  const [base, setBase] = useState<ThresholdBase>('ELIGIBLE');
  const [blank, setBlank] = useState(false);
  const [durationText, setDurationText] = useState('');

  useEffect(() => {
    if (!open) return;
    setSelected(race?.candidate_ids ?? suggestedCandidateIds(state, positionId));
    setTopX('');
    setPctText(String(race ? Number(race.threshold_pct) : Number(e.threshold_pct)));
    setBase((race?.threshold_base ?? e.threshold_base) as ThresholdBase);
    setBlank(race?.allow_blank ?? e.allow_blank);
    const d = race ? race.duration_minutes : e.duration_minutes;
    setDurationText(d ? String(d) : '');
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reset only when the dialog opens
  }, [open]);

  const previous = useMemo(
    () => state.races.filter((r) => r.position_id === positionId && r.status === 'CLOSED' && r.result_data).length > 0,
    [state, positionId],
  );

  const pct = Number(pctText.replace(',', '.'));
  const duration = durationText.trim() === '' ? null : Number(durationText);
  const invalid =
    selected.length < 2 ||
    !(pct > 0 && pct <= 100) ||
    (duration !== null && !(Number.isInteger(duration) && duration > 0));

  const toggle = (id: string) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const applyTop = () => {
    const n = Number(topX);
    if (Number.isInteger(n) && n >= 2) setSelected(suggestedCandidateIds(state, positionId, n));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{race ? 'Editar ronda' : 'Nueva ronda'} · {positionTitle}</DialogTitle>
          <DialogDescription>Elige quiénes participan en esta ronda y sus reglas. Se pueden cambiar hasta abrirla.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <div className="flex flex-wrap items-end justify-between gap-2">
              <Label>Candidatos de la ronda ({selected.length})</Label>
              {previous && (
                <div className="flex items-center gap-2">
                  <Input className="h-8 w-16" inputMode="numeric" placeholder="Top" value={topX} onChange={(ev) => setTopX(ev.target.value)} />
                  <Button size="sm" variant="outline" type="button" onClick={applyTop}>Pasan los primeros</Button>
                </div>
              )}
            </div>
            {pool.length === 0 ? (
              <p className="text-sm text-muted-foreground">No hay candidatos disponibles. Agrégalos en la pestaña Candidatos.</p>
            ) : (
              <ul className="max-h-64 divide-y overflow-auto rounded-md border">
                {pool.map((c) => (
                  <li key={c.id}>
                    <label className="flex cursor-pointer items-center gap-3 p-2.5 hover:bg-muted/50">
                      <Checkbox checked={selected.includes(c.id)} onCheckedChange={() => toggle(c.id)} />
                      <MinisterPhoto filename={c.photo_filename} name={c.full_name} className="size-8" />
                      <span className="text-sm">{c.full_name}</span>
                    </label>
                  </li>
                ))}
              </ul>
            )}
            {selected.length < 2 && <p className="text-xs text-destructive">Se necesitan al menos 2 candidatos.</p>}
          </div>

          <div className="space-y-3 rounded-md border p-3">
            <p className="text-sm font-medium">Reglas de esta ronda</p>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="rd-pct">Porcentaje para ganar</Label>
                <Input id="rd-pct" inputMode="decimal" className="w-24" value={pctText} onChange={(ev) => setPctText(ev.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="rd-dur">Duración (minutos)</Label>
                <Input id="rd-dur" inputMode="numeric" className="w-28" placeholder="Sin límite" value={durationText} onChange={(ev) => setDurationText(ev.target.value)} />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Porcentaje sobre</Label>
              <Select value={base} onValueChange={(x) => setBase(x as ThresholdBase)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="ELIGIBLE">Habilitados para votar</SelectItem>
                  <SelectItem value="VALID">Votos válidos</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center justify-between gap-3">
              <Label htmlFor="rd-blank">Mostrar "Voto en blanco"</Label>
              <Switch id="rd-blank" checked={blank} onCheckedChange={setBlank} />
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button
            disabled={busy || invalid}
            onClick={() => onSubmit(selected, { threshold_pct: pct, threshold_base: base, allow_blank: blank, duration_minutes: duration })}
          >
            {busy && <Loader2 className="mr-2 size-4 animate-spin" />}
            {race ? 'Guardar ronda' : 'Crear ronda'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
